/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { ChangeEvent, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  TextField,
  Typography,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableFooter,
} from '@mui/material';

import {
  GetChangeDto,
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
  ProcessListForChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
/**
 * The internal dependencies
 */
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  getTotalTime,
  getSumOfWP,
  formatNumber,
} from '../../utils/getTotalTime';
import LoadSpinnerSmall from '../LoadSpinner/LoadSpinnerSmall';
import { getDepartmentById } from '../../utils/utils';
import { useProjectStore } from '../../store/projectStore';
import {
  isChangeManager,
  useUserHasFullAccess,
} from '../../utils/permissionHelper';
import WorkPackageRow from './WorkPackageRow';
import ProcessItemInfo from '../ProcessItems/ProcessItemInfo';
import ProcessAccordionTitle from '../ProcessItems/ProcessAccordionTitle';

type ProcessCardProps = {
  process: ProcessListForChangeDto;
  workPackages: ListWorkPackageDto[];
  change: GetChangeDto | null;
  handleChangeProcess: (fieldName: string, value: number, id: number) => void;
  handleActiveProcess: (id: number) => void;
  editable: boolean;
  isActive: boolean;
  isLoading: boolean;
  workMaterials: ListWorkMaterialDto[] | [];
  workServices: ListWorkServiceDto[];
};

const ProcessTable = observer(
  ({
    process,
    workPackages,
    workMaterials,
    workServices,
    change,
    isActive,
    isLoading,
    handleChangeProcess,
    handleActiveProcess,
    editable,
  }: ProcessCardProps) => {
    const { pspPhases } = useProjectStore();
    const { t } = useTranslation();
    const [processHours, setProcessHours] = useState(
      process.calculatedHours
        ? process.calculatedHours
        : getTotalTime(workPackages),
    );

    const department = getDepartmentById(
      process.pId,
      change?.commonProject?.navProcessToCommonProjectRelations,
    );

    const hourlyRate = process.finalHourlyRate
      ? process.finalHourlyRate
      : department?.hourlyRate;

    const isManager = isChangeManager(change);

    const showReleasedHours =
      change?.status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
      change?.status ===
        UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress ||
      change?.status ===
        UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved ||
      change?.status === UpdateChangeDtoStatusEnum.Check ||
      change?.status === UpdateChangeDtoStatusEnum.Closed ||
      change?.status === UpdateChangeDtoStatusEnum.InternalApprovalRejected;

    const editReleasedHours =
      change?.status === UpdateChangeDtoStatusEnum.CalcComplete ||
      change?.status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
      change?.status ===
        UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress ||
      change?.status ===
        UpdateChangeDtoStatusEnum.ExternalChangeInternallyApproved;

    const onChange = (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const { target } = e;
      const { value } = target;
      handleActiveProcess(process.id);
      if (target.name === 'calculatedHours') setProcessHours(Number(value));

      handleChangeProcess(target.name, Number(value), process.id);
    };
    const { calculatedHours, timeImpact, releasedHours } = process;

    const getWorkPackagePhase = (workPackage: ListWorkPackageDto) =>
      pspPhases.find(p => p.id === workPackage.navPhasePspId);

    const userHasFullAccess = useUserHasFullAccess(change);

    return (
      <Accordion
        css={css`
          background-color: white;
          margin-bottom: 16px;
          width: 98%;
          &::before {
            display: none;
          }

          & .MuiAccordionSummary-gutters {
            padding-left: 10px;
          }
          &.Mui-expanded {
            margin: 0;
            margin-bottom: 16px;
          }
        `}
      >
        <AccordionSummary
          aria-controls="panel1-content"
          id="panel1-header"
          expandIcon={
            <ExpandMoreIcon
              color="primary"
              css={css`
                transform: rotate(-90deg);
              `}
            />
          }
          css={css`
            flex-direction: row-reverse;
            gap: 16px;
            height: 70px;
            align-items: center;

            & .MuiAccordionSummary-content ~ .Mui-expanded {
              transform: rotate(90deg) !important;
            }
          `}
        >
          {department && <ProcessAccordionTitle department={department} />}

          <ProcessItemInfo
            workPackages={workPackages}
            calculatedHours={calculatedHours}
            releasedHours={releasedHours}
            timeImpact={timeImpact}
            workServices={workServices}
            workMaterials={workMaterials}
            process={process}
            showCalculation={false}
            showImplementation={false}
            userHasFullAccess={userHasFullAccess}
          />
        </AccordionSummary>
        <AccordionDetails>
          {isLoading && isActive && <LoadSpinnerSmall />}

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('workPackage.workPackages')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {t('costs.hourlyRate')} [€/h]
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {t('workPackage.estimatedHoursKTM')} [h]
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {t('costs.costs')} [€]
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workPackages.map(wp => (
                  <WorkPackageRow
                    key={wp.navPhasePspId}
                    wp={wp}
                    phaseName={getWorkPackagePhase(wp)?.name || ''}
                    hourlyRate={hourlyRate ?? 0}
                  />
                ))}
                <TableRow sx={{ borderTop: '2px solid grey' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('process.sumeval')}
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {formatNumber(getTotalTime(workPackages), 0)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {formatNumber(getSumOfWP(workPackages, hourlyRate || 0))}
                  </TableCell>
                </TableRow>
              </TableBody>
              <TableFooter sx={{ backgroundColor: 'white' }}>
                <TableRow>
                  <TableCell>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {t('calc.calculatedHours')}
                    </Typography>
                    <TextField
                      css={css`
                        width: 100%;
                        margin-top: 16px;
                        .MuiInput-underline:before {
                          border-bottom: 1px solid #ececec;
                        }
                        .MuiInput-input {
                          padding: 4px 0 4px;
                        }
                        .MuiInputLabel-asterisk {
                          color: #ff0000;
                        }
                      `}
                      required
                      name="calculatedHours"
                      type="number"
                      defaultValue={processHours}
                      disabled={
                        !editable ||
                        !(
                          change?.status ===
                            UpdateChangeDtoStatusEnum.EvalAccepted ||
                          change?.status ===
                            UpdateChangeDtoStatusEnum.EvalCalcRejected
                        )
                      }
                      label={t('calc.hoursCalc')}
                      variant="standard"
                      onChange={onChange}
                      InputProps={{
                        style: {
                          paddingLeft: '16px',
                          paddingBottom: '6px',
                          marginBottom: '16px',
                          fontSize: '14px',
                          fontWeight: '300',
                        },
                        endAdornment: (
                          <InputAdornment position="end">h</InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        style: {
                          paddingLeft: '20px',
                          fontWeight: 300,
                          color: '#333333',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 'bold', paddingTop: 3 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {formatNumber(
                        (process.calculatedHours || 0) * (hourlyRate || 0),
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Row for "Released Hours" (conditionally rendered) */}
                {showReleasedHours && (
                  <TableRow>
                    <TableCell>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {t('process.customerreleasedHours')}
                      </Typography>
                      <TextField
                        css={css`
                          width: 100%;
                          .MuiInput-underline:before {
                            border-bottom: 1px solid #ececec;
                          }
                          .MuiInput-input {
                            padding: 4px 0 4px;
                          }
                          .MuiInputLabel-asterisk {
                            color: #ff0000;
                          }
                        `}
                        name="releasedHours"
                        required
                        disabled={!isManager || !editReleasedHours}
                        defaultValue={process.releasedHours}
                        type="number"
                        label={t('process.releasedHours')}
                        variant="standard"
                        onBlur={onChange}
                        InputProps={{
                          style: {
                            paddingLeft: '16px',
                            paddingBottom: '6px',
                            marginBottom: '16px',
                            fontSize: '14px',
                            fontWeight: '300',
                          },
                          endAdornment: (
                            <InputAdornment position="end">h</InputAdornment>
                          ),
                        }}
                        InputLabelProps={{
                          style: {
                            paddingLeft: '20px',
                            fontWeight: 300,
                            color: '#333333',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                      {/* Placeholder for estimated hours, which is not applicable here */}
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {formatNumber(
                          (process.releasedHours || 0) * (hourlyRate || 0),
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableFooter>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  },
);

export default ProcessTable;
